import React, { useMemo, useState } from "react";
import {
  buildMonthDays,
  compareCalendarRows,
  getDayPrimaryStatus,
  getRowDisplayStatus,
  getStatusColors,
  rowsOnDay,
  sameDay,
} from "../../utils/assessmentCalendarRules";
import {
  COMPACT_DAY_SIZING,
  getAssessmentDensityBarClass,
  getCompactDayDotLimit,
  getCompactDayNumberCircleClasses,
  getCompactSizingKey,
  getMultiMonthGridColsClass,
  getVisibleCalendarMonths,
} from "../../utils/calendarMultiMonth";
import { getCompactDayCellClasses } from "../../utils/calendarDayCell";
import CalendarDayCell from "./CalendarDayCell";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

function CompactCalendarMonthBlock({
  year,
  month,
  rows,
  isAdmin,
  today,
  selectedDate,
  onSelectDate,
  calendarViewSpan,
  hoverKey,
  setHoverKey,
}) {
  const sizingKey = getCompactSizingKey(calendarViewSpan);
  const sizing = COMPACT_DAY_SIZING[sizingKey];
  const monthDate = useMemo(() => new Date(year, month, 1), [year, month]);
  const days = useMemo(() => buildMonthDays(monthDate), [monthDate]);
  const dotLimit = getCompactDayDotLimit(calendarViewSpan);

  const monthAssessmentCount = useMemo(
    () =>
      days.reduce((sum, day) => {
        if (!day.inMonth) return sum;
        return sum + rowsOnDay(rows, day.date).length;
      }, 0),
    [days, rows]
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
      <div className="mb-1 flex items-baseline justify-between gap-2 px-0.5">
        <h4 className="truncate text-xs font-semibold text-gray-900">
          {monthDate.toLocaleString("en-US", { month: "short" })}{" "}
          <span className="font-normal text-gray-500">{year}</span>
        </h4>
        {monthAssessmentCount > 0 ? (
          <span className="flex-shrink-0 text-[10px] tabular-nums text-gray-400">
            {monthAssessmentCount}
          </span>
        ) : null}
      </div>
      <div
        className={`mb-0.5 grid grid-cols-7 leading-none text-gray-400 ${sizing.weekday}`}
      >
        {WEEKDAYS.map((label, idx) => (
          <div key={`${label}-${idx}`} className="text-center">
            {label}
          </div>
        ))}
      </div>
      <div className="relative isolate z-20 grid grid-cols-7 gap-px overflow-visible rounded bg-gray-100 ring-1 ring-gray-100">
        {days.map((day, dayIdx) => {
          const dayRows = rowsOnDay(rows, day.date);
          const assessmentCount = dayRows.length;
          const hasAssessments = assessmentCount > 0;
          const dayPrimaryStatus = getDayPrimaryStatus(dayRows, { isAdmin, today });
          const selected = sameDay(day.date, selectedDate);
          const isToday = sameDay(day.date, today);
          const cellHoverKey = `${year}-${month}-${day.key}`;

          return (
            <CalendarDayCell
              key={day.key}
              date={day.date}
              dayIdx={dayIdx}
              rows={dayRows}
              selected={selected}
              isAdmin={isAdmin}
              today={today}
              isHovered={hoverKey === cellHoverKey}
              onHoverChange={(hovered) => setHoverKey(hovered ? cellHoverKey : null)}
              onSelectDate={onSelectDate}
              className={getCompactDayCellClasses({
                inMonth: day.inMonth,
                isToday,
                hasAssessments,
                sizingClass: sizing.cell,
              })}
            >
              <time
                dateTime={day.date.toISOString().slice(0, 10)}
                className={`${getCompactDayNumberCircleClasses(
                  dayPrimaryStatus,
                  { selected, isToday, hasAssessments },
                  sizingKey
                )} flex-shrink-0`}
              >
                {day.date.getDate()}
              </time>
              {hasAssessments ? (
                <span className="pointer-events-none flex flex-shrink-0 items-center justify-center gap-0.5">
                  {[...dayRows]
                    .sort(compareCalendarRows)
                    .slice(0, dotLimit)
                    .map((assessment, idx) => {
                      const rowStatus = getRowDisplayStatus(assessment, { isAdmin, today });
                      if (!rowStatus) return null;
                      const { dot } = getStatusColors(rowStatus);
                      return (
                        <span
                          key={`${assessment.vqa_id}-${assessment.sponsor_id}-${idx}`}
                          className={`block flex-shrink-0 rounded-full ${sizing.dot} ${dot}`}
                        />
                      );
                    })}
                  {assessmentCount > dotLimit ? (
                    <span className="text-[8px] font-bold leading-none text-indigo-600">+</span>
                  ) : null}
                </span>
              ) : null}
              {hasAssessments ? (
                <span
                  className={`pointer-events-none absolute inset-x-0 bottom-0 block h-0.5 ${getAssessmentDensityBarClass(
                    assessmentCount
                  )}`}
                  aria-hidden="true"
                />
              ) : null}
            </CalendarDayCell>
          );
        })}
      </div>
    </div>
  );
}

const MultiMonthCalendarGrid = ({
  monthDate,
  calendarViewSpan,
  rows,
  isAdmin,
  today = new Date(),
  selectedDate,
  onSelectDate,
}) => {
  const [hoverKey, setHoverKey] = useState(null);
  const visibleMonths = useMemo(
    () =>
      getVisibleCalendarMonths(
        monthDate.getFullYear(),
        monthDate.getMonth(),
        calendarViewSpan
      ),
    [monthDate, calendarViewSpan]
  );
  const gridCols = getMultiMonthGridColsClass(calendarViewSpan);

  return (
    <div
      className={`mt-4 grid grid-cols-1 gap-3 ${gridCols}`}
      data-testid="multi-month-calendar-grid"
      data-month-span={calendarViewSpan}
    >
      {visibleMonths.map(({ year, month }) => (
        <CompactCalendarMonthBlock
          key={`${year}-${month}`}
          year={year}
          month={month}
          rows={rows}
          isAdmin={isAdmin}
          today={today}
          selectedDate={selectedDate}
          onSelectDate={onSelectDate}
          calendarViewSpan={calendarViewSpan}
          hoverKey={hoverKey}
          setHoverKey={setHoverKey}
        />
      ))}
    </div>
  );
};

export default MultiMonthCalendarGrid;
